import * as React from 'react';

import { TextField } from '@rematter/ui-web';
import { RefreshCw, AlertCircle } from 'lucide-react';

import { ExpressionInput } from '../../../shared/ExpressionInput';
import { NodeConfigProps } from '../../types';

import {
  LoopConfigType,
  MAX_ALLOWED_ITERATIONS,
  DEFAULT_MAX_ITERATIONS,
} from './schema';

/**
 * Configuration panel for Loop nodes.
 * Allows configuring array expression, item variable name, and max iterations.
 */
export const LoopConfig: React.FC<NodeConfigProps<LoopConfigType>> = ({
  config,
  onUpdate,
}) => {
  const [arrayExpression, setArrayExpression] = React.useState<string>(
    config.arrayExpression || '',
  );
  const [itemVariableName, setItemVariableName] = React.useState<string>(
    config.itemVariableName || 'item',
  );
  const [maxIterations, setMaxIterations] = React.useState<number>(
    config.maxIterations || DEFAULT_MAX_ITERATIONS,
  );

  const handleUpdate = React.useCallback(
    (updates: Partial<LoopConfigType>) => {
      const newConfig = { ...config, ...updates };
      onUpdate(newConfig);
    },
    [config, onUpdate],
  );

  const handleArrayExpressionChange = React.useCallback(
    (value: string) => {
      setArrayExpression(value);
      handleUpdate({ arrayExpression: value });
    },
    [handleUpdate],
  );

  const handleItemVariableNameChange = React.useCallback(
    (value: string) => {
      setItemVariableName(value);
      handleUpdate({ itemVariableName: value });
    },
    [handleUpdate],
  );

  const handleMaxIterationsChange = React.useCallback(
    (value: string) => {
      const numValue = parseInt(value, 10);
      if (!isNaN(numValue)) {
        // Clamp value between 1 and MAX_ALLOWED_ITERATIONS
        const clampedValue = Math.max(
          1,
          Math.min(numValue, MAX_ALLOWED_ITERATIONS),
        );
        setMaxIterations(clampedValue);
        handleUpdate({ maxIterations: clampedValue });
      }
    },
    [handleUpdate],
  );

  return (
    <div className="@LoopConfig space-y-4">
      <div>
        <ExpressionInput
          label="Array Expression"
          value={arrayExpression}
          onChange={handleArrayExpressionChange}
          placeholder="trigger.data.contacts"
          description="Expression that evaluates to an array to iterate over"
        />
      </div>

      <div>
        <label className="mb-1 block text-sm font-medium">
          Item Variable Name
        </label>
        <p className="mb-2 text-xs text-neutral/60">
          Name for the current item in each iteration
        </p>
        <TextField
          value={itemVariableName}
          onChange={handleItemVariableNameChange}
          placeholder="item"
          className="font-mono"
        />
        <p className="mt-1 text-xs text-neutral/60">
          Must start with a letter and contain only letters, numbers, or
          underscores
        </p>
      </div>

      <div>
        <label className="mb-1 block text-sm font-medium">Max Iterations</label>
        <p className="mb-2 text-xs text-neutral/60">
          Maximum number of iterations (for security)
        </p>
        <TextField
          type="number"
          value={maxIterations.toString()}
          onChange={handleMaxIterationsChange}
        />
        <p className="mt-1 text-xs text-neutral/60">
          Default: {DEFAULT_MAX_ITERATIONS}, Max: {MAX_ALLOWED_ITERATIONS}
        </p>
      </div>

      <div className="rounded-lg border bg-neutral/5 p-3">
        <p className="mb-2 text-xs font-medium">Loop Context Variables:</p>
        <div className="space-y-1 font-mono text-xs text-neutral/70">
          <div>
            <code>loop.index</code> - Current iteration (0-based)
          </div>
          <div>
            <code>loop.count</code> - Total number of items
          </div>
          <div>
            <code>loop.first</code> - True on first iteration
          </div>
          <div>
            <code>loop.last</code> - True on last iteration
          </div>
          <div>
            <code>{itemVariableName || 'item'}</code> - The current item
          </div>
        </div>
      </div>

      <div className="rounded-lg border bg-neutral/5 p-3">
        <p className="mb-2 text-xs font-medium">Expression Examples:</p>
        <div className="space-y-1 font-mono text-xs text-neutral/70">
          <div>
            <code>trigger.data.contacts</code>
          </div>
          <div>
            <code>outputs.fetchData.results</code>
          </div>
          <div>
            <code>trigger.data.orderItems</code>
          </div>
        </div>
      </div>

      <div className="rounded-lg border border-warning/20 bg-warning/10 p-3">
        <div className="flex items-start gap-2">
          <AlertCircle className="mt-0.5 size-4 shrink-0 text-warning" />
          <div>
            <p className="text-xs font-medium text-warning">
              Performance Warning
            </p>
            <p className="mt-1 text-xs text-warning">
              Loops are limited to {MAX_ALLOWED_ITERATIONS} iterations for
              security and performance. Large arrays may timeout or consume
              excessive resources.
            </p>
          </div>
        </div>
      </div>

      <div className="rounded-lg border bg-neutral/5 p-3">
        <p className="mb-2 text-xs font-medium">Common Use Cases:</p>
        <div className="space-y-1 text-xs text-neutral/70">
          <div>• Send emails to a list of contacts</div>
          <div>• Process each item in an order</div>
          <div>• Create records for each entry in an array</div>
          <div>• Validate multiple data points</div>
        </div>
      </div>

      <div className="rounded-lg border border-primary/20 bg-primary/10 p-3">
        <div className="flex items-start gap-2">
          <RefreshCw className="mt-0.5 size-4 shrink-0 text-primary" />
          <div>
            <p className="text-xs text-primary">
              <strong>How it works:</strong> All nodes after the Loop will
              execute once for each item in the array. Use the item variable
              name and loop context to access iteration data.
            </p>
          </div>
        </div>
      </div>

      <div className="rounded-lg border border-primary/20 bg-primary/10 p-3">
        <p className="text-xs text-primary">
          <strong>Tip:</strong> Access the current item with{' '}
          <code className="rounded bg-primary/20 px-1">
            {itemVariableName || 'item'}
          </code>{' '}
          and iteration info with{' '}
          <code className="rounded bg-primary/20 px-1">loop.index</code>,{' '}
          <code className="rounded bg-primary/20 px-1">loop.count</code>, etc.
        </p>
      </div>
    </div>
  );
};
